import { Card, EmptyState } from '@hgc/ui';
import type { JSX } from 'react';

import { MapPanel } from '../../map/MapPanel';
import { PlannedWorkCard, SectionHeader, type PlannedItem } from './Placeholder';

/**
 * 计划分区里**还没接进来**的部分。
 *
 * 地图（本分区最该出现的东西）已经接进来了，见下面的 `MapPanel`。
 * 剩下的航点编辑与围栏编辑依赖"编辑结果能上传到机体"，而那需要 mission 管理器。
 * 画出一堆既不能拖也不能上传的假航点，比空着更容易误导 —— 空着至少是诚实的。
 */
const PLANNED: readonly PlannedItem[] = [
  { title: '航点编辑：新增/拖动/删除、批量修改高度与速度', owner: 'plan-waypoint-editor' },
  { title: '航线上传与下载（含 MISSION_ITEM_INT 的精度处理）', owner: 'mission 管理器' },
  { title: '地理围栏与返航点编辑', owner: 'plan-waypoint-editor' },
];

/**
 * 计划分区。
 *
 * ## 为什么这里先接地图
 *
 * 计划分区是"在地图上想清楚要飞哪里"，没有地图的航线规划等于在表格里填经纬度。
 * 地图引擎因此是这一格里第一个必须就位的东西：它提供机体位置、航迹、返航点与可插拔底图，
 * 并且**证明接线是通的** —— 这些数据来自真实遥测，不是摆出来的假数据。
 *
 * ## 后续接入（`plan-waypoint-editor`）
 *
 * 航点编辑、地理围栏与返航点编辑由 `plan-waypoint-editor` 填充：
 * 它们的编辑结果最终要上传到机体，而那依赖还没接进来的 mission 管理器。
 * 这些图层由地图引擎的 `MapEngine.setTarget()` 与 `MapSurface.setData()` 接入 ——
 * 调用方自带图层 id 与描述，换底图后引擎会自动重挂（见 `layers.ts` 的 `OVERLAY_ID_PREFIX`）。
 */
export function PlanSection(): JSX.Element {
  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <SectionHeader title="计划" summary="航线规划、航线上传/下载与地理围栏。" />

      <MapPanel />

      <Card title="航点编辑还没有接进来">
        <EmptyState
          level="info"
          title="地图已经可用，但航点还改不了"
          description={
            '这一格现在能用的是地图本身：机体位置、航迹、返航点、底图切换与跟随都接在真实遥测上。' +
            '航点编辑（新增/拖动/删除、批量改高度与速度）与地理围栏、返航点编辑还没有接进来 —— ' +
            '它们的编辑结果最终要上传到机体，而那依赖正在并发开发的 mission 管理器。' +
            '在它就绪之前画出来的航点既不能拖也不能上传，那比空着更容易误导。'
          }
        />
      </Card>

      <PlannedWorkCard items={PLANNED} />
    </div>
  );
}
